import { Alert, Form } from 'antd';
import { useEffect, useState } from 'react';
import type { RootStateOrAny } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { login } from '../../actions/auth';
import drawnArrow from '../../assets/image/hand-drawn-arrow.png';
import loginStar from '../../assets/image/login-star.png';
import logo from '../../assets/image/logo_1000px.png';
import Button from '../../components/Button/Button';
import Input from '../../components/Form/FormItem/Input/Input';
import { IAuthState } from '../../reducers/auth';
import { FlexContainer } from '../../styles/Global';
import { mediaQuery } from '../../styles/styled';

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isAuthenticated } = useSelector(
    (state: RootStateOrAny) => state.auth as IAuthState
  );

  const [showErr, setShowErr] = useState(false);

  const onFinish = async (values: any) => {
    setShowErr(false);

    dispatch(
      login(
        values,
        () => {},
        () => {
          setShowErr(true);
        }
      )
    );
  };

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated]);

  return (
    <Wrapper>
      <LoginContainer>
        <Logo src={logo} alt="HD Sticker" />
        <LoginTitle>Login</LoginTitle>
        <FormContainer>
          <Form
            name="normal_login"
            className="login-form"
            onFinish={onFinish}
            autoComplete="off"
          >
            {showErr && (
              <Alert
                message="Username or password is wrong"
                type="error"
                className="m__b--16"
                showIcon
              />
            )}
            <Label>Username*</Label>
            <Form.Item
              name="username"
              rules={[{ required: true, message: 'Enter your username' }]}
            >
              <Input placeholder="Username" />
            </Form.Item>

            <Label>Password*</Label>
            <Form.Item
              name="password"
              rules={[{ required: true, message: 'Enter your password' }]}
            >
              <Input type="password" placeholder="Password" />
            </Form.Item>

            <Form.Item>
              <Button fullWidth htmlType="submit" className="login-form-button">
                Sign In
              </Button>
            </Form.Item>
          </Form>
        </FormContainer>
        <CopyrightContainer>
          <p>© HD Sticker 2025</p>
          <p>help@hdsticker.com</p>
        </CopyrightContainer>
      </LoginContainer>
      <RightContainer>
        <img
          className="login-star"
          src={loginStar}
          alt="Login Star"
          width={80}
          height={80}
        />
        <h1>Start turning your ideas into reality.</h1>
        <p>
          Create a free account and get full access to all features for 30-days.
          No credit card needed. Get started in 2 minutes.
        </p>
      </RightContainer>
      <DrawnArrow
        className="drawn-arrow"
        src={drawnArrow}
        alt="Drawn Arrow"
        width={300}
      />
    </Wrapper>
  );
};

export default Login;

const Wrapper = styled.div`
  display: block;

  width: 100%;
  height: 100%;
  min-height: 100vh;
  /* display: flex; */
  align-items: center;
  justify-content: center;

  ${mediaQuery.lg`
    display: grid;
    grid-template-columns: 1fr 1fr;
  `}
`;

const RightContainer = styled.div`
  display: none;

  h1 {
    font-size: 48px;
    justify-content: center;
    font-weight: 500;
    line-height: 60px;
    margin-bottom: 24px;
  }

  p {
    font-size: 18px;
    font-weight: 500;
    line-height: 28px;
  }

  .login-star {
    margin-bottom: 32px;
  }

  ${mediaQuery.lg`
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 0 50px 0 100px;
    height: 100%;
    background-image: linear-gradient(to top, #051d4d, #073693);
    color: #ffffff;
  `}
`;

const DrawnArrow = styled.img`
  display: none;

  ${mediaQuery.lg`
    width: 300px;
    position: absolute;
    bottom: 7%;
    left: 50%;
    transform: translateX(-50%);
  `}
`;

const Logo = styled.img`
  width: 240px;
  margin-top: 100px;
  margin-bottom: 40px;

  ${mediaQuery.lg`
    margin-top: 0;
    margin-bottom: 80px;
  `}
`;

const LoginContainer = styled.div`
  min-height: 100vh;
  height: 100%;
  background-color: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  border-radius: 20px;
  padding: 40px;
  position: relative;
  justify-content: start;

  .login-form {
    width: 360px;
  }

  ${mediaQuery.lg`
    justify-content: center;
    min-height: unset;
  `}
`;

const Title = styled.p`
  font-size: 34px;
  font-weight: 300;
`;

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;

  .ant-form-item {
    margin-bottom: 20px;
  }
`;

const Label = styled.p`
  margin-bottom: 6px;
  color: var(--color-black-bolder);
  font-size: 14px;
  text-align: left;
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  width: 300px;
  margin: 10px;
`;

const LoginTitle = styled.h2`
  font-size: 30px;
  font-weight: 600;
  line-height: 38px;
  color: #000000;
  margin-bottom: 24px;
`;

const CopyrightContainer = styled(FlexContainer)`
  width: 100%;
  position: absolute;
  bottom: 0;
  padding: 30px;

  p {
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    color: var(--color-light-grey);
  }
`;
